import React, { createContext, useContext, useState, useEffect } from 'react';
import { getRanDbDashboard } from '../functions/get_ran_db_dashboard.function';

const RanDbDashboardContext = createContext();

export const useRanDbDashboard = () => {
  const context = useContext(RanDbDashboardContext);
  if (!context) {
    throw new Error('useRanDbDashboard must be used within a RanDbDashboardProvider');
  }
  return context;
};

export const RanDbDashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Cache duration: 5 minutes (300000 ms)
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchDashboardData = async (forceRefresh = false) => {
    // Check if we have cached data and it's still valid
    if (!forceRefresh && dashboardData.length > 0 && lastFetchTime) {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      if (timeSinceLastFetch < CACHE_DURATION) {
        console.log('Using cached ranDb dashboard data');
        return dashboardData;
      }
    }

    // Don't fetch if already loading
    if (loading) {
      console.log('RanDb dashboard data already loading, skipping...');
      return dashboardData;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('Fetching fresh ranDb dashboard data...');

      const response = await getRanDbDashboard();
      const data = Array.isArray(response) ? response : [];

      setDashboardData(data);
      setLastFetchTime(Date.now());
      setIsInitialized(true);

      console.log('RanDb dashboard data fetched successfully:', data.length, 'sites');
      return data;
    } catch (error) {
      console.error('Error fetching ranDb dashboard data:', error);
      setError(error);
      return dashboardData; // Return cached data if available
    } finally {
      setLoading(false);
    }
  };

  const refreshDashboardData = () => {
    return fetchDashboardData(true);
  };

  const clearCache = () => {
    setDashboardData([]);
    setLastFetchTime(null);
    setIsInitialized(false);
  };

  const value = {
    dashboardData,
    loading,
    error,
    isInitialized,
    fetchDashboardData,
    refreshDashboardData,
    clearCache,
    lastFetchTime,
  };

  return <RanDbDashboardContext.Provider value={value}>{children}</RanDbDashboardContext.Provider>;
};
